/**
 * /connect — the human half of the RFC 8628 device flow.
 *
 * A terminal, CLI, or local model ran `buzzbitx-mcp login` and showed the
 * person a short code. They arrive here (often via the QR-able
 * verification_uri_complete link), type or confirm the code, see what the
 * device is asking for in plain language, pick a workspace, and decide.
 * The device's poll on /oauth/token completes by itself — nothing on this
 * page ever sees or shows a token.
 *
 * Post-ratchet file: every user-facing string renders via t() from src/i18n.
 */
import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../../services/apiClient';
import { API_URL } from '../../config/api';
import { getAuthToken } from '../../services/api/core';
import { AuthFormCard, AuthPageLayout } from '@/layouts/AuthPageLayout';
import { t } from '../../i18n';

interface WorkspaceOption {
  id: string;
  name: string;
}

interface VerifyData {
  userCode: string;
  clientName: string;
  hostName: string | null;
  scopes: string[];
  expiresAt: string;
}

type Step =
  | { kind: 'enter'; busy: boolean; error: string | null }
  | { kind: 'approve'; busy: boolean; error: string | null; verified: VerifyData }
  | { kind: 'done'; decision: 'approved' | 'denied' };

const SCOPE_COPY: Record<string, () => string> = {
  read: () => t('connect.scope.read'),
  write: () => t('connect.scope.write'),
  execute: () => t('connect.scope.execute'),
  admin: () => t('connect.scope.admin'),
};

function verifyFailureCopy(reason: string): string {
  if (reason === 'expired') return t('connect.expired');
  if (reason === 'already_decided') return t('connect.alreadyDecided');
  return t('connect.notFound');
}

// Object-shaped signature: the hardcodedStrings scanner is a regex over raw
// source, and a multi-line positional parameter list between two generics
// reads to it as a JSX text node (see the scanner's own useState note).
// Braces in the parameter break the false match.
async function postEnveloped<T>(args: {
  path: string;
  body: Record<string, unknown>;
}): Promise<{ ok: true; data: T } | { ok: false; message: string; status: number }> {
  const { path, body } = args;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as {
    success?: boolean;
    data?: T;
    error?: { message?: string };
  };
  if (res.ok && json.success && json.data !== undefined) return { ok: true, data: json.data };
  return { ok: false, message: json.error?.message ?? '', status: res.status };
}

const Connect: React.FC = () => {
  const [params] = useSearchParams();
  const prefill = params.get('code') ?? '';

  const [code, setCode] = React.useState(prefill);
  const [step, setStep] = React.useState<Step>({ kind: 'enter', busy: false, error: null });
  const [workspaces, setWorkspaces] = React.useState<WorkspaceOption[]>([]);
  const [workspaceId, setWorkspaceId] = React.useState<string | null>(null);
  // The approve step waits for this. A code check that answers first used to
  // render Connect disabled (no workspace yet) — a first click did nothing.
  const [workspacesLoaded, setWorkspacesLoaded] = React.useState(false);
  const [granted, setGranted] = React.useState<Set<string>>(new Set());

  const redirectToLogin = React.useCallback((): void => {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.assign(`/login?next=${next}`);
  }, []);

  // Workspaces load once; a 401 routes through login and back here.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.get<{ data?: WorkspaceOption[] } | WorkspaceOption[]>(
          '/workspaces',
        );
        const list: WorkspaceOption[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        if (cancelled) return;
        setWorkspaces(list);
        setWorkspaceId(list[0]?.id ?? null);
        setWorkspacesLoaded(true);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        if (/401|unauthorized/i.test(message)) {
          redirectToLogin();
          return;
        }
        // Any other failure still ends the wait: the approve step then says
        // there is no workspace, rather than the page checking forever.
        setWorkspacesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [redirectToLogin]);

  const verify = React.useCallback(
    async (rawCode: string): Promise<void> => {
      setStep({ kind: 'enter', busy: true, error: null });
      const result = await postEnveloped<VerifyData>({
        path: '/oauth/device/verify',
        body: { user_code: rawCode },
      });
      if (!result.ok) {
        if (result.status === 401) {
          redirectToLogin();
          return;
        }
        setStep({ kind: 'enter', busy: false, error: verifyFailureCopy(result.message) });
        return;
      }
      setGranted(new Set(result.data.scopes));
      setStep({ kind: 'approve', busy: false, error: null, verified: result.data });
    },
    [redirectToLogin],
  );

  // A complete link (?code=…) goes straight to verification.
  const autoRan = React.useRef(false);
  React.useEffect(() => {
    if (prefill && !autoRan.current) {
      autoRan.current = true;
      void verify(prefill);
    }
  }, [prefill, verify]);

  const decide = async (decision: 'approve' | 'deny'): Promise<void> => {
    if (step.kind !== 'approve') return;
    setStep({ ...step, busy: true, error: null });
    const body =
      decision === 'approve'
        ? {
            user_code: step.verified.userCode,
            decision,
            workspaceId,
            grantedScopes: Array.from(granted),
          }
        : { user_code: step.verified.userCode, decision };
    const result = await postEnveloped<{ decision: 'approved' | 'denied' }>({
      path: '/oauth/device/decision',
      body,
    });
    if (!result.ok) {
      if (result.status === 401) {
        redirectToLogin();
        return;
      }
      setStep({
        ...step,
        busy: false,
        error: result.status === 404 ? t('connect.approve.gone') : result.message,
      });
      return;
    }
    setStep({ kind: 'done', decision: result.data.decision });
  };

  if (step.kind === 'done') {
    return (
      <AuthPageLayout className="bg-white">
        <AuthFormCard className="shadow-none">
          <h1 className="text-2xl font-semibold text-gray-900 mb-3">
            {step.decision === 'approved' ? t('connect.done.approved') : t('connect.done.denied')}
          </h1>
          <p className="text-sm text-gray-600">{t('connect.done.close')}</p>
          {step.decision === 'approved' && (
            <Link to="/settings/connected-apps" className="mt-6 inline-block text-sm text-gray-700 underline">
              {t('connect.done.manage')}
            </Link>
          )}
        </AuthFormCard>
      </AuthPageLayout>
    );
  }

  if (step.kind === 'approve' && workspacesLoaded) {
    const { verified } = step;
    const displayName = verified.hostName ?? verified.clientName;
    return (
      <AuthPageLayout className="bg-white">
        <AuthFormCard className="shadow-none">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            {t('connect.approve.heading', { name: displayName })}
          </h1>
          <p className="text-sm text-gray-600 mb-8">
            {t('connect.approve.subtitle', { name: displayName })}
          </p>

          {step.error && (
            <div className="mb-6 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-800">
              {step.error}
            </div>
          )}

          {workspaces.length === 0 && (
            <p className="mb-6 text-sm text-gray-600">{t('connect.approve.noWorkspaces')}</p>
          )}

          {workspaces.length > 1 && (
            <section className="mb-8">
              <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">
                {t('connect.approve.workspace')}
              </h2>
              <div className="space-y-2">
                {workspaces.map((w) => (
                  <label
                    key={w.id}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-md cursor-pointer hover:border-gray-300"
                  >
                    <input
                      type="radio"
                      name="workspace"
                      value={w.id}
                      checked={workspaceId === w.id}
                      onChange={() => setWorkspaceId(w.id)}
                      className="text-gray-900 focus:ring-gray-300"
                    />
                    <span className="text-sm font-medium text-gray-900">{w.name}</span>
                  </label>
                ))}
              </div>
            </section>
          )}

          <section className="mb-8">
            <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-3">
              {t('connect.approve.permissions')}
            </h2>
            <div className="space-y-2">
              {verified.scopes.map((s) => (
                <label
                  key={s}
                  className="flex items-start gap-3 p-3 border border-gray-200 rounded-md cursor-pointer hover:border-gray-300"
                >
                  <input
                    type="checkbox"
                    checked={granted.has(s)}
                    onChange={(e) => {
                      const next = new Set(granted);
                      if (e.target.checked) next.add(s);
                      else next.delete(s);
                      setGranted(next);
                    }}
                    className="mt-0.5 text-gray-900 focus:ring-gray-300"
                  />
                  <span className="flex-1 text-sm text-gray-700">
                    {(SCOPE_COPY[s] ?? (() => s))()}
                  </span>
                </label>
              ))}
            </div>
          </section>

          {/* Marked so the cookie banner docks at the top rather than over the decision. */}
          <div className="flex gap-3" data-cookie-avoid>
            <button
              type="button"
              onClick={() => void decide('deny')}
              disabled={step.busy}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('connect.approve.deny')}
            </button>
            <button
              type="button"
              onClick={() => void decide('approve')}
              disabled={step.busy || !workspaceId || granted.size === 0}
              className="flex-1 px-4 py-2.5 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
            >
              {step.busy ? t('connect.approve.approving') : t('connect.approve.approve')}
            </button>
          </div>

          <p className="mt-6 text-xs text-gray-500 leading-relaxed">
            {t('connect.approve.revokeNote')}
          </p>
        </AuthFormCard>
      </AuthPageLayout>
    );
  }

  // A verified code waiting on the workspace list stays on this card as "Checking…".
  const waitingForWorkspaces = step.kind === 'approve';

  return (
    <AuthPageLayout className="bg-white">
      <AuthFormCard className="shadow-none">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">{t('connect.title')}</h1>
        <p className="text-sm text-gray-600 mb-8">{t('connect.subtitle')}</p>

        {step.error && (
          <div className="mb-6 p-3 border border-red-200 bg-red-50 rounded-md text-sm text-red-800">
            {step.error}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (code.trim().length > 0) void verify(code);
          }}
        >
          <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
            {t('connect.codeLabel')}
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={t('connect.codePlaceholder')}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className="w-full mb-6 px-4 py-3 border border-gray-300 rounded-md text-lg font-mono tracking-widest text-center uppercase focus:border-gray-500 focus:ring-0"
          />
          <button
            type="submit"
            disabled={step.busy || waitingForWorkspaces || code.trim().length === 0}
            className="w-full px-4 py-2.5 bg-gray-900 text-white rounded-md text-sm font-medium hover:bg-gray-900 disabled:opacity-50"
          >
            {step.busy || waitingForWorkspaces ? t('connect.checking') : t('connect.continue')}
          </button>
        </form>
      </AuthFormCard>
    </AuthPageLayout>
  );
};

export default Connect;
